import { useState } from "react";
import {
  Alert,
  Button,
  Card,
  FileButton,
  Group,
  Stack,
  Table,
  Text,
  TextInput,
  Title,
} from "@mantine/core";
import { useMutation, useQuery } from "@tanstack/react-query";

import { deleteDoctorSignature, putDoctorSignature, type SignatureAnswer } from "@/api/branding";
import { listPortalUsers, type PortalUser } from "@/api/portal";
import { useResolvedPermissions } from "@/auth/permissions";
import {
  IMAGE_MAX_EDGE_PX,
  SIGNATURE_MAX_BYTES,
  checkImage,
  fileBytes,
  formatBytes,
  type ImageRefusal,
} from "@/branding/images";
import { t, type MessageKey } from "@/i18n";

/**
 * Doctors' signatures, set or removed by the lab's client admin (plan B1 and B7, contract
 * 4.7). A doctor can do the same for themselves on My signature.
 *
 * **Reach is the server's.** The orchestrator refuses a doctor whose signature belongs to an
 * organisation outside the admin's own and the ones under it, whatever this page sends. A
 * first signature is filed under the organisation open on this page.
 *
 * The doctors listed are the users IAM returns for the admin's own organisation who hold a
 * lab Clinician group (`GG-SmartReports-<lab>-Clinician`). That list needs the client portal
 * permission and covers only the organisation the admin signed in to, so it is shown only
 * there, and any other doctor is reached by their IAM user id. There is no read of another
 * user's signature, so the page says what is on file only after it has set or removed one.
 */

const REFUSAL_TEXT: Record<ImageRefusal, MessageKey> = {
  image_type_refused: "branding.image_type_refused",
  image_too_large: "branding.image_too_large",
  image_dimensions: "branding.image_dimensions",
};

const CLINICIAN_GROUP = /-clinician$/i;

/** Users holding a lab Clinician group. A user whose groups were not read is left out. */
export function cliniciansOf(users: PortalUser[]): PortalUser[] {
  return users.filter(
    (u) => u.groups !== null && u.groups.some((g) => CLINICIAN_GROUP.test(g.group_name ?? "")),
  );
}

interface DoctorSignaturesCardProps {
  organizationId: string;
  /** True when the open organisation is the one the admin signed in to, in the portal. */
  listClinicians: boolean;
}

export function DoctorSignaturesCard({
  organizationId,
  listClinicians,
}: DoctorSignaturesCardProps): JSX.Element {
  const permissions = useResolvedPermissions();
  const canList = listClinicians && permissions.has("clients:manage");
  const users = useQuery({
    queryKey: ["portal-users"],
    queryFn: listPortalUsers,
    enabled: canList,
    retry: false,
  });
  const [typedId, setTypedId] = useState("");
  const clinicians = cliniciansOf(users.data?.users ?? []);

  return (
    <Card withBorder data-testid="doctor-signatures">
      <Title order={3} mb="xs">
        {t("doctor_signatures.title")}
      </Title>
      <Text size="sm" c="dimmed" mb="sm">
        {t("doctor_signatures.hint")}
      </Text>
      <Stack gap="md">
        {canList &&
          (users.isPending ? (
            <Text size="sm" c="dimmed">
              {t("doctor_signatures.loading")}
            </Text>
          ) : users.isError ? (
            <Alert color="yellow" p="xs" data-testid="doctor-signatures-list-error">
              {t("doctor_signatures.list_unavailable")}
            </Alert>
          ) : clinicians.length === 0 ? (
            <Text size="sm" c="dimmed" data-testid="doctor-signatures-none">
              {t("doctor_signatures.none")}
            </Text>
          ) : (
            <Table withTableBorder data-testid="doctor-signatures-list">
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>{t("doctor_signatures.doctor")}</Table.Th>
                  <Table.Th>{t("doctor_signatures.signature")}</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {clinicians.map((u) => (
                  <Table.Tr key={u.id} data-testid={`doctor-${u.id}`}>
                    <Table.Td>
                      <Text size="sm">{u.username ?? u.email ?? u.id}</Text>
                      {u.email !== null && u.username !== null && (
                        <Text size="xs" c="dimmed">
                          {u.email}
                        </Text>
                      )}
                    </Table.Td>
                    <Table.Td>
                      <SignatureControls
                        iamUserId={u.id}
                        organizationId={organizationId}
                        testId={`doctor-${u.id}`}
                      />
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          ))}
        <Stack gap="xs" data-testid="doctor-signature-by-id">
          <TextInput
            label={t("doctor_signatures.by_id")}
            description={t("doctor_signatures.by_id_hint")}
            value={typedId}
            onChange={(e) => setTypedId(e.currentTarget.value.trim())}
            w={360}
            data-testid="doctor-signature-id"
          />
          {typedId !== "" && (
            <SignatureControls
              key={typedId}
              iamUserId={typedId}
              organizationId={organizationId}
              testId="doctor-by-id"
            />
          )}
        </Stack>
        <Text size="xs" c="dimmed">
          {t("branding.image_limits")
            .replace("{cap}", formatBytes(SIGNATURE_MAX_BYTES))
            .replace("{edge}", String(IMAGE_MAX_EDGE_PX))}
        </Text>
      </Stack>
    </Card>
  );
}

function SignatureControls({
  iamUserId,
  organizationId,
  testId,
}: {
  iamUserId: string;
  organizationId: string;
  testId: string;
}): JSX.Element {
  const [problem, setProblem] = useState<string | null>(null);
  const [answer, setAnswer] = useState<SignatureAnswer | null>(null);
  const upload = useMutation({
    mutationFn: (file: File) => putDoctorSignature(iamUserId, file, organizationId),
    onMutate: () => setProblem(null),
    onSuccess: setAnswer,
  });
  const remove = useMutation({
    mutationFn: () => deleteDoctorSignature(iamUserId),
    onMutate: () => setProblem(null),
    onSuccess: setAnswer,
  });

  async function picked(file: File | null): Promise<void> {
    setProblem(null);
    if (file === null) return;
    const check = checkImage(await fileBytes(file), "signature");
    if (!check.ok) {
      setProblem(t(REFUSAL_TEXT[check.refusal]));
      return;
    }
    upload.mutate(file);
  }

  const failure = upload.error ?? remove.error;
  return (
    <Stack gap={4}>
      <Group gap="xs">
        <FileButton onChange={(f) => void picked(f)} accept="image/png,image/jpeg">
          {(props) => (
            <Button
              {...props}
              size="xs"
              variant="light"
              loading={upload.isPending}
              data-testid={`${testId}-set`}
            >
              {t("doctor_signatures.set")}
            </Button>
          )}
        </FileButton>
        <Button
          size="xs"
          variant="subtle"
          color="gray"
          loading={remove.isPending}
          onClick={() => remove.mutate()}
          data-testid={`${testId}-remove`}
        >
          {t("doctor_signatures.remove")}
        </Button>
      </Group>
      {answer !== null && (
        <Text size="xs" data-testid={`${testId}-result`}>
          {answer.sha256 !== null
            ? `${t("signature.on_file")} ${answer.version}`
            : t("doctor_signatures.removed")}
        </Text>
      )}
      {(problem !== null || failure !== null) && (
        <Alert color="red" p="xs" data-testid={`${testId}-error`}>
          {problem ?? (failure instanceof Error ? failure.message : null)}
        </Alert>
      )}
    </Stack>
  );
}
